import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import moment from "moment";
import Chart from "react-apexcharts";
import {Box, Skeleton, Stack, Tooltip, Typography} from "@mui/material";
import {ProjectStatisticRepository} from "../../../../repositories/ProjectStatisticRepository";
import {Panel, PanelHeading} from "./Panel";
import {csvFileName, exportCsv, ExportButton} from "./ExportButton";
import {DateRangePickerMenu} from "./DateRangePickerMenu";
import {QUICK_RANGES} from "../../../../components/date-range/QuickRangeMenu";

const DATE_TIME = "YYYY-MM-DDTHH:mm:ss";

// Statuses grouped into outcomes. Fixed order and colours (validated for colour-blind separation as a set).
const OUTCOMES = [
  {key: "completed", label: "Completed", color: "#2F9A6A", statuses: ["FINISHED", "SUCCESS"]},
  {key: "transferred", label: "Transferred", color: "#3D86C6", statuses: ["REDIRECTED", "HANDOVER", "AGENT_CALL_INITIATED"]},
  {key: "hungUp", label: "Hung up", color: "#D9A13A", statuses: ["HANGUP", "USER_NOT_TALKING"]},
  {key: "missed", label: "Missed / failed", color: "#C2455A", statuses: ["MISSED_CALL", "FAILED"]},
  {key: "active", label: "In progress", color: "#8A6FE0", statuses: ["ACTIVE"]},
];

const defaultRange = () => {
  const today = QUICK_RANGES.find(item => item.label === "Today");
  const [start, end] = today.calc();
  return {label: "Today", start, end};
};

const formatStatus = (status) => status.toLowerCase().replaceAll("_", " ").replace(/^\w/, c => c.toUpperCase());

const percent = (count, total) => total ? `${Math.round((count / total) * 100)}%` : "0%";

// Where the project's conversations landed (project statistic /statuses), as a donut of outcomes
export const StatusPanel = ({projectId, filters, refreshInterval}) => {
  const [range, setRange] = useState(defaultRange);
  const [statistic, setStatistic] = useState(null);
  const requestRef = useRef(0);

  const load = useCallback(async () => {
    if (!projectId || !range?.start || !range?.end) return;
    const requestId = ++requestRef.current;
    try {
      const response = await ProjectStatisticRepository.getStatuses({
        projectId,
        filters,
        start: moment(range.start).format(DATE_TIME),
        end: moment(range.end).format(DATE_TIME),
      });
      if (requestId === requestRef.current) setStatistic(response?.data || {counts: {}, total: 0});
    } catch (e) {
      console.error("Error fetching statuses:", e);
      if (requestId === requestRef.current) setStatistic(prev => prev || {counts: {}, total: 0});
    }
  }, [projectId, filters, range]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!refreshInterval) return;
    const intervalId = setInterval(load, refreshInterval);
    return () => clearInterval(intervalId);
  }, [load, refreshInterval]);

  const outcomes = useMemo(() => OUTCOMES.map(outcome => {
    const breakdown = outcome.statuses.map(status => ({status, count: Number(statistic?.counts?.[status]) || 0}));
    return {...outcome, breakdown, count: breakdown.reduce((sum, item) => sum + item.count, 0)};
  }), [statistic]);

  const total = Number(statistic?.total) || outcomes.reduce((sum, outcome) => sum + outcome.count, 0);

  const options = {
    chart: {type: "donut", animations: {enabled: false}, sparkline: {enabled: true}},
    labels: outcomes.map(outcome => outcome.label),
    colors: outcomes.map(outcome => outcome.color),
    // 2px surface gap between segments
    stroke: {width: 2, colors: ["#fff"]},
    dataLabels: {enabled: false},
    legend: {show: false},
    plotOptions: {pie: {donut: {size: "76%", labels: {show: false}}, expandOnClick: false}},
    states: {hover: {filter: {type: "darken", value: 0.9}}, active: {filter: {type: "none"}}},
    tooltip: {
      custom: ({seriesIndex}) => {
        const outcome = outcomes[seriesIndex];
        return `<div style="padding:8px 12px;background:#fff;border:1px solid #ECECF3;border-radius:10px;font-family:Inter,sans-serif;white-space:nowrap;">
          <div style="font-size:11px;color:#8A8AA0;">${outcome.label}</div>
          <div style="font-size:13px;font-weight:600;color:#23264D;">${outcome.count.toLocaleString()} · ${percent(outcome.count, total)}</div>
        </div>`;
      },
    },
  };

  return (
    <Panel sx={{p: 2.5}}>
      <PanelHeading
        kicker={"OUTCOMES"}
        title={"Conversation status"}
        description={"Where conversations land"}
        actions={<>
          <ExportButton
            disabled={!statistic}
            onClick={() => exportCsv([
              ["Outcome", "Status", "Count", "Share (%)"],
              ...outcomes.flatMap(outcome => outcome.breakdown.map(item => [
                outcome.label, item.status, item.count, total ? ((item.count / total) * 100).toFixed(1) : "0.0",
              ])),
            ], csvFileName(`conversation-status-${range.label.toLowerCase().replace(/\s+/g, "-")}`))}
          />
          <DateRangePickerMenu
            value={range}
            onChange={(next) => setRange(next || defaultRange())}
            allowClear={false}
            ariaLabel={"Status range"}
          />
        </>}
      />

      <Box sx={{position: "relative", width: 200, height: 200, mx: "auto", my: 2.5}}>
        {statistic === null
          ? <Skeleton variant={"circular"} width={200} height={200}/>
          : total > 0
            ? <Chart options={options} series={outcomes.map(outcome => outcome.count)} type={"donut"} width={200} height={200}/>
            : <Box sx={{width: 200, height: 200, borderRadius: "50%", border: "24px solid #F2F2F7", boxSizing: "border-box"}}/>}
        <Stack alignItems={"center"} justifyContent={"center"} sx={{position: "absolute", inset: 0, pointerEvents: "none"}}>
          <Typography sx={{fontSize: 28, fontWeight: 700, color: "#23264D", letterSpacing: "-0.02em", lineHeight: 1.1}}>
            {statistic === null ? "–" : total.toLocaleString()}
          </Typography>
          <Typography sx={{fontSize: 12, color: "#8A8AA0"}}>Conversations</Typography>
        </Stack>
      </Box>

      <Stack gap={0.25}>
        {outcomes.map(outcome => (
          <Tooltip
            key={outcome.key}
            placement={"left"}
            title={
              <Box>
                {outcome.breakdown.map(item => (
                  <Box key={item.status}>{formatStatus(item.status)}: {item.count.toLocaleString()}</Box>
                ))}
              </Box>
            }
          >
            <Stack direction={"row"} alignItems={"center"} gap={1} sx={{
              px: 1, py: 0.75, mx: -1, borderRadius: "8px", "&:hover": {bgcolor: "#FAFAFD"},
            }}>
              <Box sx={{width: 8, height: 8, borderRadius: "50%", bgcolor: outcome.color, flexShrink: 0}}/>
              <Typography sx={{flex: 1, fontSize: 13, color: "#4A4A63"}}>{outcome.label}</Typography>
              <Typography sx={{fontSize: 12, color: "#8A8AA0"}}>{outcome.count.toLocaleString()}</Typography>
              <Typography sx={{width: 40, textAlign: "right", fontSize: 13, fontWeight: 600, color: "#23264D"}}>
                {percent(outcome.count, total)}
              </Typography>
            </Stack>
          </Tooltip>
        ))}
      </Stack>
    </Panel>
  );
};
